import { sleep } from '../utils/sleep.js'

export interface RetryOptions<TResult = unknown> {
  /**
   * @default Infinity
   */
  retries?: number // default: Infinity
  /**
   * @default 500
   */
  delayMs?: number
  /**
   * @default 1 (1 = fixed, 2 = exponential)
   */
  backoff?: number // default: 1 (1 = fixed, 2 = exponential)
  shouldRetry?: (error: unknown, attempt: number) => boolean

  /**
   * If provided, this function will be called when the function succeeds, instead of returning the result.
   */
  onSuccess?: <T extends TResult>(result: T) => void

  /**
   * If provided, this function will be called when the function fails, instead of throwing an error.
   *
   */
  onFailure?: (error: unknown) => void
}

export async function retry<TResult = unknown>(
  fn: () => Promise<TResult>,
  {
    retries = Infinity,
    delayMs = 500,
    backoff = 1,
    shouldRetry = () => true,
    onFailure,
    onSuccess,
  }: RetryOptions<TResult> = {},
): Promise<TResult> {
  let attempt = 0

  while (attempt < retries) {
    try {
      // eslint-disable-next-line no-await-in-loop
      const result = await fn()
      if (onSuccess) {
        onSuccess(result)
        return undefined
      }

      return result
    } catch (error) {
      attempt += 1

      if (attempt >= retries || !shouldRetry(error, attempt)) {
        if (onFailure) {
          onFailure(error)
          return undefined
        }

        throw error
      }

      const wait = delayMs * backoff ** (attempt - 1)
      // eslint-disable-next-line no-await-in-loop
      await sleep(wait)
    }
  }

  if (onFailure) {
    onFailure(new Error('Retry failed'))
    return undefined
  }

  throw new Error('Retry failed')
}
